import React, { useEffect } from "react";

import getContentById from "../../../../shared/utils/getContentById";
import _ from "lodash";
import Backgrounds from "../../_partials/Backgrounds/Index";
import TemplateBlock from "../../_partials/TemplateBlock/Index";
import prepareVideoBg from "../../../../shared/functions/prepareVideoBg";
import animationScript, {
  animationCleanUp,
} from "./animation";

import assets from "./assets";
import getOptaCountryCode from "../../../../shared/utils/getOptaCountryCode";
import countries from "../../../../../language/de/countries.json";
import { useTranslation } from "react-i18next";
// import outroVideo9x16 from "../../../assets/outros/outro_playoffs_9x16.webm";
import Image from "../../../../shared/components/Image";
import {
  getDynamicLogoPath,
  toIsoDateString,
} from "../../../../shared/utils/functions";
import { getShortPlayerName } from "../../../../shared/feeds/worldcup/playerHelper";
import { WcBgCircle } from "../../../../shared/backgrounds/WcBgCircle/Index";
import ProgressBar from "../../../../shared/components/ProgressBar";
import { MatchScoreWidget } from "../../_partials/MatchScoreWidget/MatchScoreWidgetSD";
import PitchTacticsForSD from "../../_partials/PitchTactics/PitchTacticsForSD";

require("./style/style.scss");
require("./style/animation.scss");

function getPositionGroup(pos) {
  if (pos === "TW") return "GK";

  if (
    pos.startsWith("IV") ||
    pos === "LV" ||
    pos === "RV" ||
    pos.endsWith("AV")
  ) {
    return "DEF";
  }

  if (
    pos.startsWith("DM") ||
    pos.startsWith("DL") ||
    pos.startsWith("DR")
  ) {
    return "DM";
  }

  if (
    pos === "ZO" ||
    pos === "ZM" ||
    pos.startsWith("ZM") ||
    pos === "OLM" ||
    pos === "ORM"
  ) {
    return "MID";
  }

  if (
    pos.startsWith("OH") ||
    pos.startsWith("OM") ||
    pos === "HL" ||
    pos === "HR"
  ) {
    return "ATT";
  }

  if (
    pos.startsWith("ST") ||
    pos === "LS" ||
    pos === "RS" ||
    pos === "MS"
  ) {
    return "ST";
  }

  return "MID";
}

function getHorizontalSortIndex(pos) {
  if (!pos) return 50;

  const explicitOrder = {
    TW: 50,
    LV: 10,
    DL: 10,
    IVL: 30,
    IVZ: 50,
    IV: 50,
    IVR: 70,
    RV: 90,
    DR: 90,
    DML: 20,
    DMZ: 50,
    DMR: 80,
    ZML: 25,
    ZM: 50,
    ZMZ: 50,
    ZO: 50,
    ZMR: 75,
    OLM: 20,
    ORM: 80,
    HLL: 15,
    HL: 50,
    HRR: 85,
    HR: 85,
    OHL: 20,
    OHM: 50,
    OHR: 80,
    LS: 25,
    STZ: 50,
    ST: 50,
    MS: 50,
    RS: 75,
  };

  if (explicitOrder[pos] !== undefined) {
    return explicitOrder[pos];
  }

  if (pos.startsWith("DL") || pos.endsWith("VL")) return 15;
  if (pos.startsWith("DR") || pos.endsWith("VR")) return 85;
  if (pos.startsWith("IV")) return 50;
  if (pos.endsWith("L")) return 25;
  if (pos.endsWith("R")) return 75;
  if (pos.endsWith("Z") || pos.endsWith("M")) return 50;

  return 50;
}

function sortPlayersByHorizontalPosition(players) {
  return [...players].sort((a, b) => {
    const orderDiff =
      getHorizontalSortIndex(a.PlayingPosition) -
      getHorizontalSortIndex(b.PlayingPosition);

    if (orderDiff !== 0) return orderDiff;

    return (Number(a.ShirtNumber) || 0) - (Number(b.ShirtNumber) || 0);
  });
}

function buildFormationMap(players, formation) {
  const result = {};

  const goalkeeper = players.find(
    p => p.PlayingPosition === "TW"
  );

  result[0.9] = goalkeeper ? [goalkeeper] : [];

  const grouped = {
    DEF: [],
    DM: [],
    MID: [],
    ATT: [],
    ST: [],
  };

  players
    .filter(p => p.PlayingPosition !== "TW")
    .forEach(player => {
      const group = getPositionGroup(
        player.PlayingPosition
      );

      if (grouped[group]) {
        grouped[group].push(player);
      }
    });

  const start = 0.05;
  const end = 0.7;

  const step =
    formation.length === 1
      ? 0
      : (end - start) / (formation.length - 1);

  let lineDefinitions;

  switch (formation.join("-")) {
    case "4-4-2":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        grouped.ST,
      ];
      break;

    case "4-3-3":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        [...grouped.ATT, ...grouped.ST],
      ];
      break;

    case "4-2-3-1":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        [...grouped.ATT, ...grouped.MID],
        grouped.ST,
      ];
      break;

    case "4-1-4-1":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        [...grouped.MID, ...grouped.ATT],
        grouped.ST,
      ];
      break;

    case "3-5-2":
      lineDefinitions = [
        grouped.DEF,
        [
          ...grouped.DM,
          ...grouped.MID,
          ...grouped.ATT,
        ],
        grouped.ST,
      ];
      break;

    case "3-4-3":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        [...grouped.ATT, ...grouped.ST],
      ];
      break;

    case "3-4-2-1":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "5-3-2":
      lineDefinitions = [
        [...grouped.DEF, ...grouped.DM],
        [...grouped.MID, ...grouped.ATT],
        grouped.ST,
      ];
      break;

    case "5-4-1":
      lineDefinitions = [
        [...grouped.DEF, ...grouped.DM],
        [...grouped.MID, ...grouped.ATT],
        grouped.ST,
      ];
      break;

    case "4-5-1":
      lineDefinitions = [
        grouped.DEF,
        [
          ...grouped.DM,
          ...grouped.MID,
          ...grouped.ATT,
        ],
        grouped.ST,
      ];
      break;

    case "4-2-2-2":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        [...grouped.ATT, ...grouped.MID],
        grouped.ST,
      ];
      break;

    case "4-1-2-1-2":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        grouped.MID,
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "4-3-1-2":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "3-4-1-2":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "3-4-3_DIAMOND":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        grouped.MID,
        [...grouped.ATT, ...grouped.ST],
      ];
      break;

    case "4-4-1-1":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "4-3-2-1":
      lineDefinitions = [
        grouped.DEF,
        [...grouped.MID, ...grouped.DM],
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "5-2-3":
      lineDefinitions = [
        [...grouped.DEF, ...grouped.DM],
        grouped.MID,
        [...grouped.ATT, ...grouped.ST],
      ];
      break;

    case "5-2-1-2":
      lineDefinitions = [
        [...grouped.DEF, ...grouped.DM],
        grouped.MID,
        grouped.ATT,
        grouped.ST,
      ];
      break;

    case "3-1-4-2":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        [...grouped.MID, ...grouped.ATT],
        grouped.ST,
      ];
      break;

    case "4-1-3-2":
      lineDefinitions = [
        grouped.DEF,
        grouped.DM,
        [...grouped.MID, ...grouped.ATT],
        grouped.ST,
      ];
      break;

    default:
      lineDefinitions = [
        grouped.DEF,
        grouped.MID,
        grouped.ST,
      ];
  }

  formation.forEach((count, idx) => {
    const pos = Number(
      (start + idx * step).toFixed(2)
    );

    const source = lineDefinitions[idx] || [];

    result[pos] = sortPlayersByHorizontalPosition(source.slice(0, count));
  });

  return result;
}

const Index = (props) => {
  const refs = {
    player: React.createRef(),
    team: React.createRef(),
  };

  const {
    animation,
    data,
    images: Images,
    apiSetup,
    mode,
    format,
    isTimeline,
  } = props;

  const id = `${_.get(data, "template_name", "")}_${_.get(data, "uuid", "")}`;
  const { t } = useTranslation();

  const prepareAnimation = () => {
    prepareVideoBg({ id });
  };

  useEffect(() => {
    let animationReturn = null;
    if (mode === "animation" || mode === "video") {
      if (animation) {
        prepareAnimation();
        animationReturn = animationScript({
          refs,
          data,
          animation,
          mode,
          id,
          format,
          apiSetup,
          templateName: data?.template_name,
        });
      }
    }

    //handle outro video BEGIN
    // if(mode === "video" && !isTimeline) {
    //   const outroVideo = $(`#${id} video.outro-optional-video`);
    //   outroVideo.get(0).addEventListener("loadedmetadata", function() {
    //       const that = this;
    //       setTimeout(()=> {
    //         outroVideo?.css('opacity', 1);
    //         this.currentTime = 0;
    //         that.play();
    //       }, frontendCfg.showOutroAfter)
    //     }, false);
    // }
    //handle outro video END

    return function cleanUp() {
      if (animationReturn) {
        animationCleanUp(animationReturn);
      }
    };
  }, [animation, mode]);

  const templateData = _.get(data, "data");

  const { options, fields, dynamic } = templateData;
  const headline = getContentById("headline", fields);

  // const nextGame = _.get(dynamic, "nextGame");
  const matchInfo = _.get(dynamic, "matchInfo");
  const lineups = _.get(dynamic, "lineup");

  const competitionId = matchInfo?.competition?.id;

  const thisGameDate = new Date(
    toIsoDateString(matchInfo?.date, matchInfo?.time),
  );

  const backgroundImage = getMainBackground(format, _.get(dynamic, "teamSide"));

  let homeTeam = matchInfo?.TeamStatistic?.[0];
  let awayTeam = matchInfo?.TeamStatistic?.[1];

  const teamSide = getContentById("selectedSide", fields);

  const lineupData =
    teamSide === "home"
      ? _.find(lineups, { Role: 'home' })
      : _.find(lineups, { Role: 'guest' });
  const lineUpHome = _.find(lineups, { Role: 'home' });
  const lineUpAway = _.find(lineups, { Role: 'guest' });
  const formationUsed = lineupData?.LineUp;
  let firstTeamOfficial = lineupData?.TrainerStaff?.Trainer;
  if (Array.isArray(firstTeamOfficial)) {
    firstTeamOfficial = firstTeamOfficial.find((f) => f.Role === 'headcoach');
  }
  const coach = firstTeamOfficial?.Shortname ? (
    <div>
      {firstTeamOfficial?.Shortname}
    </div>
  ) : firstTeamOfficial?.FirstName ?(
    <div>
      {firstTeamOfficial?.FirstName}{" "}
      {firstTeamOfficial?.LastName}
    </div>
  ) : "-";
  if (!homeTeam) {
    homeTeam = lineUpHome;
  }
  if (!awayTeam) {
    awayTeam = lineUpAway;
  }
  const mainPlayers = lineupData?.Players?.Player?.filter((p) => p?.Starting === 'true');
  const formation = lineupData?.LineUp?.split(" ")[0]?.split("-")?.map(Number);

  let lines;
  if (formation && mainPlayers?.length > 0) {
    lines = buildFormationMap(mainPlayers, formation)
  }
  return (
    <div
      id={id}
      className={`T_${_.get(data, "template_name", "")} ${animation ? "animation" : ""} side-${_.get(dynamic, "teamSide")}`}
    >
      <TemplateBlock>
        <Backgrounds bg1={backgroundImage} loop={true} />
        {/*<WcBgCircle />*/}

        <div className={"wc-content wc-content-v2"}>
          <ProgressBar height={"8px"} strokeColor="#177C76" />
          <div className={"wc-bg-content"}></div>

          <div className={"left-block-side"}>
            <div className={"teams-block " + teamSide}>
              {teamSide === "home" ? (
                <div className={"team-logo-container home"}>
                  <Image
                    className="team-logo"
                    src={getDynamicLogoPath({ team: homeTeam, competitionId })}
                  />
                  <div className={"team-name home"}>
                    {t(homeTeam?.ShortName || homeTeam?.name, {
                      ns: "countries",
                    })}
                  </div>
                </div>
              ) : (
                <div className={"team-logo-container away"}>
                  <Image
                    className="team-logo"
                    src={getDynamicLogoPath({ team: awayTeam, competitionId })}
                  />

                  <div className={"team-name away"}>
                    {t(awayTeam?.ShortName || awayTeam?.name, {
                      ns: "countries",
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={"group"}>
            {matchInfo?.series?.name.replace(
              "Group",
              t("Group", { keyPrefix: "templates" }),
            ) || ""}
          </div>
          <MatchScoreWidget
            homeTeam={homeTeam}
            awayTeam={awayTeam}
            matchInfo={matchInfo}
          />

          <div className={"coach-info"}>
            <div className={"group"}>
              {t("Coach", { keyPrefix: "templates" })}
            </div>
            <div className={"vs-team-info"}>{coach}</div>
          </div>

          <div className={"right-block-side"}>
            <div className={"wc-headline"}>
              {t("Lineup", { keyPrefix: "templates" })}
            </div>

            <img className={"heatmap-pitch"} src={assets.heatmap_pitch_fcb} />
            <div className={"lineup-tactics"}>
              {
                formation && lines &&
                <PitchTacticsForSD
                  className={"lineup-pitch"}
                  formation={formation.join('')}
                  lines={lines}
                  trikot={assets.trikot}
                  side={teamSide}
                  // teamId={teamId}
                  lineup={mainPlayers}
                />
              }

            </div>

            <div className={"lineup"}>
              <div className={"lineup-content"}>
                {lineupData?.Players?.Player?.filter((p) => p.Starting === "true")
                  .map((p) => {
                    return (
                      <div key={`l${p.playerId}`} className={"player"}>
                        <div className={"shirt-number"}>{p.ShirtNumber}</div>
                        <div className={"player-name"}>
                          {p?.Shortname}
                          {/* {getShortPlayerName(p, p?.playerId === "abr79wsl0folgkyvl821ggs2c", p?.playerId !== "abr79wsl0folgkyvl821ggs2c")} */}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className={"lineup lineup-reserve"}>
              <div className={"lineup-content"}>
                {lineupData?.Players?.Player?.filter((p) => p.Starting !== "true")
                  .map((p) => {
                    return (
                      <div key={`l${p.playerId}`} className={"player"}>
                        <div className={"shirt-number"}>{p.ShirtNumber}</div>
                        <div className={"player-name"}>
                          {p?.Shortname}
                          {/* {getShortPlayerName(p, p?.playerId === "abr79wsl0folgkyvl821ggs2c", p?.playerId !== "abr79wsl0folgkyvl821ggs2c")} */}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </TemplateBlock>
    </div>
  );
};

const getMainBackground = (format, side) => {
  return _.get(assets, `sd_background_split`);
};

export default Index;